import { Card, Radio, Select, Tag } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import DatePicker from "shared/components/Form/DatePicker";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { bindInputProps } from "utils/helpers/input";
import AgeBracketManager from "../Components/AgeBracketManager";
import RegimeManager from "../Components/RegimeManager";
import ConditionsManager from "../Components/ConditionsManager";
import ZoneManager from "../Components/ZoneManager";

const PRICING_MODES = [
  {
    value: "PACKAGED",
    label: "Forfaitaire",
    description: "Prix unique pour l'ensemble du plan",
  },
  {
    value: "COMPOSABLE",
    label: "Composable",
    description: "Prix calculé à partir des garanties individuelles",
  },
];

const RISK_OPTIONS = [
  { value: "health", label: "Santé" },
  { value: "terminal", label: "Décès" },
  { value: "pro", label: "Professionnel" },
  { value: "provident", label: "Prévoyance" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Actif" },
  { value: "DRAFT", label: "Brouillon" },
  { value: "INACTIVE", label: "Inactif" },
  { value: "RETIRED", label: "Retiré" },
];

const ZONE_TYPES = [
  { value: "DEPARTMENT", label: "Par département" },
  { value: "ZIP_CODE", label: "Par code postal" },
  { value: "FREE_FORM", label: "Libre" },
];

const PlanDetails = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  data,
}) => {
  const [updatePlan] = useMutation(graphql.mutations.UPDATE_INSURANCE_PLAN);
  const planId = data.id;

  const saveField = (field, value) => {
    updatePlan({
      variables: {
        where: { id: planId },
        data: { [field]: value },
      },
    });
  };

  const handleBlurSave = (field) => {
    saveField(field, get(values, field));
  };

  const handleDirectSave = (field, value) => {
    setFieldValue(field, value);
    saveField(field, value);
  };

  return (
    <div className="configure-step">
      <Card title="Informations générales" className="m-bottom-16">
        <div className="grid--2">
          <Input
            {...bindInputProps({
              name: "name",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="simple"
            label="Nom du plan"
            placeholder="Saisir le nom du plan"
            required
            onBlur={() => handleBlurSave("name")}
          />
          <Input
            {...bindInputProps({
              name: "identifier",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="simple"
            label="Identifiant"
            placeholder="Saisir l'identifiant"
            required
            onBlur={() => handleBlurSave("identifier")}
          />
        </div>
        <div className="m-top-16">
          <Input
            {...bindInputProps({
              name: "description",
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
            })}
            type="textarea"
            label="Description"
            placeholder="Décrire le plan d'assurance"
            onBlur={() => handleBlurSave("description")}
          />
        </div>
        <div className="grid--2 m-top-16">
          <div>
            <label className="input__label">Type de risque</label>
            <Radio.Group
              value={get(values, "risk")}
              onChange={(e) => handleDirectSave("risk", e.target.value)}
            >
              {RISK_OPTIONS.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          <div>
            <label className="input__label">Statut</label>
            <Radio.Group
              value={get(values, "status")}
              onChange={(e) => handleDirectSave("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </div>
        <div className="m-top-16">
          <label className="input__label">Période</label>
          <DatePicker
            name="date"
            placeholder="Select period"
            placeholders={["Date de début", "Date de fin"]}
            defaultValue={
              get(values, "startDate") &&
              get(values, "endDate") && [
                dayjs(get(values, "startDate")),
                dayjs(get(values, "endDate")),
              ]
            }
            onChange={(_, dates) => {
              setFieldValue("startDate", dates[0] || null);
              setFieldValue("endDate", dates[1] || null);
              updatePlan({
                variables: {
                  where: { id: planId },
                  data: {
                    startDate: dates[0] || null,
                    endDate: dates[1] || null,
                  },
                },
              });
            }}
          />
        </div>
      </Card>

      <Card title="Mode de tarification" className="m-bottom-16">
        <p className="m-bottom-16" style={{ color: "#8A92A5" }}>
          Choisissez le mode de tarification de ce plan
        </p>
        <div className="d-flex" style={{ gap: "16px" }}>
          {PRICING_MODES.map((mode) => {
            const isSelected = get(values, "pricingMode") === mode.value;
            return (
              <div
                key={mode.value}
                className={`pricing-mode-card${isSelected ? " pricing-mode-card--selected" : ""}`}
                onClick={() => handleDirectSave("pricingMode", mode.value)}
                style={{ flex: 1, padding: 20, borderRadius: 12 }}
              >
                <div className="d-flex justify--between">
                  <h4>{mode.label}</h4>
                  {isSelected && <Tag color="blue">Selected</Tag>}
                </div>
                <p>{mode.description}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <ConditionsManager
        params={get(values, "params") || []}
        planId={data.id}
        setFieldValue={setFieldValue}
      />

      <Card title="Zones tarifaires" className="m-bottom-16">
        <div className="m-bottom-16">
          <label className="input__label">Type de zone</label>
          <Select
            value={get(values, "zoneType")}
            onChange={(val) => handleDirectSave("zoneType", val)}
            style={{ width: 250 }}
            placeholder="Sélectionner le type de zone..."
            allowClear
          >
            {ZONE_TYPES.map((t) => (
              <Select.Option key={t.value} value={t.value}>
                {t.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <ZoneManager
          zones={get(values, "zones") || []}
          zoneType={get(values, "zoneType")}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </Card>

      <div className="m-top-16">
        <AgeBracketManager
          ageBrackets={get(values, "ageBrackets") || []}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </div>

      <div className="m-top-16">
        <RegimeManager
          regimes={get(values, "regimes") || []}
          planId={planId}
          setFieldValue={setFieldValue}
        />
      </div>
    </div>
  );
};

export default PlanDetails;
